import { useState } from 'react'
import { usePageHeaders } from '@/src/hooks/use-page-headers/usePageHeaders'
import { useUserPreferences } from '@/src/hooks/use-user-preference-port/useUserPreferencePort'
import { userPreferencesPort } from '@/infra/config/beans'
import { VpButton, VpSkeleton } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { VpDivider } from '@vtmn-play/react/VpDivider'
import { SearchBar } from '@/src/components/search-bar/SearchBar'
import { useQuery } from '@tanstack/react-query'
import { FamilyItem } from '@/src/components/diagnoses/new/FamilyItem'
import { useStartDiagnosis } from '@/src/hooks/use-start-diagnosis-and-navigate/useStartDiagnosis'
import { getFamilies } from '@/infra/api/generated/families/families'
import { FamilyDto } from '@/infra/api/generated/model'

const { findFamilies } = getFamilies()

const SelectFamily = () => {
  const { t } = useTranslation('common')
  const { team, store } = useUserPreferences(userPreferencesPort)
  usePageHeaders('pages.select-family.title', team?.name)
  const [query, setQuery] = useState('')
  const { data, isLoading } = useQuery({
    queryKey: ['families', store, team],
    queryFn: () => findFamilies(store?.id!, team?.id!),
    enabled: !!store && !!team,
  })
  const families = data ?? []

  const filtered = families.filter(
    (family) =>
      family.id.toString().includes(query) ||
      family.name.toLowerCase().includes(query.toLowerCase())
  )

  const familyList = query.trim().length > 0 ? filtered : families

  const [selectedFamily, setSelectedFamily] = useState<FamilyDto | null>(null)
  const { startDiagnosis, isPending } = useStartDiagnosis()

  const handleSelect = (family: FamilyDto) => setSelectedFamily(family)

  const loader = Array.from({ length: 8 }, () => crypto.randomUUID()).map((key) => (
    <VpSkeleton
      key={key}
      className="mb-3 mx-5"
      variant="graphic"
      shape="squared"
      style={{ minHeight: '64px' }}
    />
  ))

  const handleContinue = () => {
    if (selectedFamily && store?.id && team?.id) {
      startDiagnosis(selectedFamily, store.id, team.id, 'my-hub')
    }
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-t-2xl">
      <div className="sticky top-0 z-50 bg-white px-5 pt-5 pb-3 rounded-t-2xl">
        <SearchBar label={t('pages.select-family.search')} value={query} onChange={setQuery} />
      </div>

      {isLoading && loader}
      <div className="flex-1 overflow-y-auto px-5 pb-6 flex flex-col">
        {familyList.map((family) => (
          <div key={family.id}>
            <div className="flex items-center justify-between">
              <FamilyItem
                onClick={() => handleSelect(family)}
                selected={selectedFamily?.id === family.id}
                label={`${family.id} - ${family.name}`}
                isPriority={family.isPriority}
              />
            </div>
            <VpDivider />
          </div>
        ))}
      </div>
      <div className="sticky bottom-0 px-5 pb-3">
        <VpButton
          className="w-full"
          disabled={!selectedFamily || isPending}
          shape="squared"
          data-testid="start-diag-button"
          onClick={handleContinue}
        >
          {t('pages.select-family.start-diagnosis')}
        </VpButton>
      </div>
    </div>
  )
}

export default SelectFamily
